<template>
  <div @click="handlePropagation">
    <draggable
      :list="items"
      :item-key="getDragId"
      :animation="150"
      handle=".reward-item-drag-handle"
      ghost-class="ghost"
      :disabled="items.length < 2"
    >
      <template #item="{ element: item, index }">
        <div :class="$style.row">
          <VdIcon
            store="fas"
            name="grip-lines"
            custom-size="16px"
            :class="[$style.dragHandle, 'reward-item-drag-handle']"
            :style="{ visibility: items.length > 1 ? 'visible' : 'hidden' }"
          />
          <Select
            v-model="item.selectedItem"
            :options="filteredItems"
            option-label="name"
            placeholder="Select an item"
            append-to="self"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" :class="$style.itemOption">
                <img
                  v-if="slotProps.value.sprite?.imageUrl"
                  :alt="slotProps.value.name"
                  :src="slotProps.value.sprite.imageUrl"
                  :class="$style.sprite"
                />
                <div>{{ slotProps.value.name }}</div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div :class="$style.itemOption">
                <img
                  v-if="slotProps.option.sprite"
                  :alt="slotProps.option.name"
                  :src="slotProps.option.sprite.imageUrl"
                  :class="$style.sprite"
                />
                <div>{{ slotProps.option.name }}</div>
              </div>
            </template>
          </Select>
          <InputNumber
            v-model="item.quantity"
            :min="1"
            placeholder="Quantity"
            :disabled="item.selectedItem?.type === 'NON_CONSUMABLE'"
            style="flex: 1"
          />
          <VdTooltip v-if="item.selectedItem?.type === 'NON_CONSUMABLE'" orientation="right" :content="tooltipContent">
            <VdIcon store="fas" name="fa-triangle-exclamation" color="var(--vd-color-yellow-500)" />
          </VdTooltip>
          <VdIcon
            v-if="items.length > 1"
            store="fas"
            name="trash"
            custom-size="20px"
            :class="$style.deleteButton"
            @click="handleRemoveItem(index)"
          />
        </div>
      </template>
    </draggable>
    <div v-if="duplicateErrorMessage" :class="$style.error">{{ duplicateErrorMessage }}</div>
    <div v-if="saveErrorMessage" :class="$style.error">{{ saveErrorMessage }}</div>
    <div :class="$style.actions">
      <Button icon="pi pi-plus" label="Add item" :class="$style.button" @click="addItem" />
      <Button label="Save" :class="$style.button" :disabled="!!duplicateErrorMessage" @click="saveItems" />
    </div>
  </div>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { useGetItemsNameAndSpriteQuery } from "@/graphql";
import { FormattedItem } from "@/modules/Rewards/types";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import { computed, onMounted, PropType, ref, watch } from "vue";
import { useRoute } from "vue-router";
import draggable from "vuedraggable";

type DraggableItem = FormattedItem & { _dragId: number };

let dragIdCounter = 0;
const nextDragId = () => {
  dragIdCounter += 1;
  return dragIdCounter;
};

const props = defineProps({
  rewardItems: {
    type: Array as PropType<FormattedItem[]>,
    default: () => [],
  },
  showConfirmation: {
    type: Boolean,
    default: false,
  },
  excludeNonConsumables: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (event: "save", items: FormattedItem[]): void;
  (event: "close"): void;
  (event: "removeItem", items: FormattedItem[]): void;
}>();

const route = useRoute();
const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);

const items = ref<DraggableItem[]>([]);
const duplicateErrorMessage = ref<string | null>(null);
const saveErrorMessage = ref<string | null>(null);

const withDragId = (item: FormattedItem): DraggableItem => ({ ...item, _dragId: nextDragId() });
const stripDragId = (item: DraggableItem): FormattedItem => ({
  selectedItem: item.selectedItem,
  quantity: item.quantity,
});
const getDragId = (item: DraggableItem) => item._dragId;

const tooltipContent =
  "When selecting a non-consumable item, the quantity<br />is automatically set to 1.<br />Price will be set to payment, and only specific IAPs<br/>associated to this item will be available. <a href='https://www.notion.so/voodoo/Supporting-Non-Consumables-with-Offers-Shop-16fa0b481db480729a96d15ba772945d' target='_blank' rel='noopener noreferrer'>More info</a>";

const { result } = useGetItemsNameAndSpriteQuery({ gameId: gameId.value, versionId: versionId.value });
const dropdownItems = computed(() => result.value?.getStoreItems || []);

const filteredItems = computed(() =>
  props.excludeNonConsumables
    ? dropdownItems.value.filter((item) => item?.type !== "NON_CONSUMABLE")
    : dropdownItems.value
);

const initializeItems = () => {
  if (props.rewardItems && props.rewardItems.length > 0) {
    items.value = props.rewardItems.map(withDragId);
  } else {
    items.value = [withDragId({ selectedItem: null, quantity: 1 })];
  }
};

watch(filteredItems, (newDropdownItems) => {
  if (newDropdownItems.length > 0) {
    initializeItems();
  }
});

onMounted(() => {
  if (filteredItems.value.length > 0) {
    initializeItems();
  }
});

const removeItem = (index: number) => {
  items.value.splice(index, 1);
  emit("removeItem", items.value.map(stripDragId));
};

const validateDuplicates = () => {
  const selectedItems = items.value.map((item) => item.selectedItem);

  const uniqueValues = new Set(selectedItems.map((v) => v?.id));
  const hasDuplicates = uniqueValues.size !== selectedItems.length;

  if (hasDuplicates) {
    duplicateErrorMessage.value = "Duplicate items selected. Please select unique items.";
  } else {
    duplicateErrorMessage.value = null;
  }
};

watch(
  items,
  (newItems) => {
    validateDuplicates();

    const nonConsumableItem = newItems.find((item) => item.selectedItem?.type === "NON_CONSUMABLE");
    if (nonConsumableItem) {
      nonConsumableItem.quantity = 1;
    }
  },
  { deep: true }
);

const addItem = () => {
  items.value.push(withDragId({ selectedItem: null, quantity: 1 }));
};

const handleRemoveItem = (index: number) => {
  if (props.showConfirmation) {
    emit("removeItem", [...items.value.slice(0, index), ...items.value.slice(index + 1)].map(stripDragId));
  } else {
    removeItem(index);
  }
};

const validateItems = () => {
  if (items.value.every((item) => item.selectedItem && item.quantity !== null)) {
    const hasQuantityGreaterThanOne = items.value.some((item) => item.quantity && item.quantity > 0);

    if (!hasQuantityGreaterThanOne) {
      saveErrorMessage.value = "At least one item must have a count greater than 0.";
      return false;
    }

    saveErrorMessage.value = null;
    return true;
  }
  saveErrorMessage.value = "Please fill all fields.";
  return false;
};

const saveItems = () => {
  if (validateItems() && !duplicateErrorMessage.value) {
    emit("save", items.value.map(stripDragId));
  }
};

const handlePropagation = (event: MouseEvent) => {
  event.stopPropagation();
};
</script>

<style module>
.row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.button {
  margin-bottom: 1rem;
}

.sprite {
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.dropdown .p-dropdown-label {
  display: flex;
  align-items: center;
}

.dropdown .p-dropdown-item {
  display: flex;
  align-items: center;
}

.sprite {
  width: 20px;
  margin-right: 0.5rem;
}

.itemOption {
  display: flex;
}

.actions {
  display: flex;
  justify-content: space-between;
}

.error {
  color: red;
  margin-bottom: 1rem;
}

.deleteButton {
  cursor: pointer;
}

.dragHandle {
  cursor: grab;
  color: var(--vd-color-text-light-2);
}

.dragHandle:active {
  cursor: grabbing;
}

:global(.ghost) {
  opacity: 0.5;
  background: var(--vd-color-blue-light-9);
}
</style>
